import axios from 'axios';
import {router} from '@inertiajs/vue3';

const API_URL_ENDPOINT = '/founder-traits-weights';

const state = {
    // All available traits
    availableTraits: [],

    // Currently selected trait IDs
    activeTraits: [],

    // Currently open trait accordion
    openTrait: null,

    // Trait importance rankings (traitId: importance)
    traitRankings: {},

    // Loading states - only ones used in the components
    isLoading: false,
    isInitialized: false,
    hasError: false,
};

const getters = {
    /**
     * Get the full trait objects for selected traits
     */
    activeTraitSections: state => {
        console.log('[founderTraits/getter] activeTraitSections called with:', {
            activeTraits: state.activeTraits,
            availableTraits: state.availableTraits ? state.availableTraits.length : 0
        });

        // Map active trait IDs to their full objects
        const result = state.activeTraits
            .map(id => {
                // Find the trait object by ID (ensure string comparison)
                const trait = state.availableTraits.find(t => String(t.id) === String(id));

                if (!trait) {
                    console.warn(`[founderTraits/getter] Could not find trait with ID ${id} in availableTraits`);
                }

                return trait;
            })
            .filter(Boolean); // Remove any undefined values

        console.log('[founderTraits/getter] activeTraitSections returning:', result.length, 'traits');
        return result;
    },

    /**
     * Check if there are any active traits
     */
    hasActiveTraits: state => state.activeTraits.length > 0,

    /**
     * Return all available traits for the selector
     */
    availableTraits: state => state.availableTraits,

    /**
     * Return active traits with importance
     * For compatibility with old code that might use this getter
     */
    selectedTraitsData: state => {
        return state.activeTraits
            .map(id => state.availableTraits.find(trait => String(trait.id) === String(id)))
            .filter(Boolean);
    }

};

const mutations = {

    /**
     * Set the available traits
     */
    SET_AVAILABLE_TRAITS(state, traits) {
        state.availableTraits = traits;
    },

    /**
     * Set the active traits
     */
    SET_ACTIVE_TRAITS(state, traitIds) {
        state.activeTraits = traitIds;
    },

    /**
     * Add a trait to the active traits
     */
    ADD_ACTIVE_TRAIT(state, traitId) {
        traitId = String(traitId); // Ensure traitId is a string
        console.log(`[founderTraits] ADD_ACTIVE_TRAIT mutation called with: ${traitId}`, {
            currentActiveTraits: [...state.activeTraits], // Create a copy for logging
            traitIdType: typeof traitId
        });

        if (!state.activeTraits.includes(traitId)) {
            state.activeTraits.push(traitId);
            console.log(`[founderTraits] Added trait ${traitId}, active traits now:`, [...state.activeTraits]);
        } else {
            console.log(`[founderTraits] Trait ${traitId} already active, no change needed`);
        }
    },

    /**
     * Remove a trait from the active traits
     */
    REMOVE_ACTIVE_TRAIT(state, traitId) {
        traitId = String(traitId); // Ensure traitId is a string
        console.log(`[founderTraits] REMOVE_ACTIVE_TRAIT mutation called with: ${traitId}`, {
            currentActiveTraits: [...state.activeTraits], // Create a copy for logging
            traitIdType: typeof traitId
        });

        const prevLength = state.activeTraits.length;
        state.activeTraits = state.activeTraits.filter(id => id !== traitId);

        if (state.activeTraits.length !== prevLength) {
            console.log(`[founderTraits] Removed trait ${traitId}, active traits now:`, [...state.activeTraits]);
        } else {
            console.log(`[founderTraits] Trait ${traitId} not found in active traits, no change needed`);
        }
    },

    /**
     * Set the open trait accordion
     */
    SET_OPEN_TRAIT(state, traitId) {
        state.openTrait = traitId;
    },

    /**
     * Set a trait's importance level
     */
    SET_TRAIT_IMPORTANCE(state, { traitId, level }) {
        state.traitRankings[traitId] = level;
    },

    /**
     * Initialize trait rankings from existing weights
     */
    INITIALIZE_TRAIT_RANKINGS(state, rankings) {
        state.traitRankings = { ...rankings };
    },

    /**
     * Clear all trait rankings
     */
    CLEAR_TRAIT_RANKINGS(state) {
        state.traitRankings = {};
        state.activeTraits = [];
    },

    /**
     * Set loading state
     */
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },

    /**
     * Set initialized state
     */
    SET_INITIALIZED(state, isInitialized) {
        state.isInitialized = isInitialized;
    },

    /**
     * Set error state
     */
    SET_ERROR(state, hasError) {
        state.hasError = hasError;
    }
};

const actions = {

    /**
     * Fetch traits and weights from API
     */
    async fetchTraitsAndWeights({ commit, dispatch }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', false);

        try {
            console.log('Fetching traits and weights from API...');

            const response = await axios.get(API_URL_ENDPOINT);
            let { traits, weights } = response.data;

            if (weights === null || weights === undefined) {
                weights = {};
            }

            console.log('Received traits:', traits.length, 'and weights:', Object.keys(weights).length);

            commit('SET_AVAILABLE_TRAITS', traits);

            // Set active traits based on weights
            const activeTraitIds = Object.keys(weights);
            commit('SET_ACTIVE_TRAITS', activeTraitIds);

            // Initialize trait rankings
            commit('INITIALIZE_TRAIT_RANKINGS', weights);

            commit('SET_INITIALIZED', true);
            return true;
        } catch (error) {
            console.error('Failed to fetch traits and weights:', error);

            commit('SET_ERROR', true);

            // Dispatch an error toast notification
            dispatch('toast/error', {
                title: 'Failed to load traits and weights',
                description: 'Please refresh the page and try again.'
            }, { root: true });

            return false;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    /**
     * Initialize the store with data from the API or props
     */
    async initializeStore({ state, dispatch, commit }, { traits = null, weights = null } = {}) {
        console.log('Initializing founder traits store with:',
            traits ? `${traits.length} traits` : 'no traits',
            weights ? `${Object.keys(weights).length} weights` : 'no weights');

        // If already initialized or loading, do nothing
        if (state.isInitialized) {
            console.log('Store already initialized, skipping');
            return;
        }

        if (state.isLoading) {
            console.log('Store already loading, skipping');
            return;
        }

        // If traits and weights are provided as props, use them
        if (traits && traits.length > 0 && weights && Object.keys(weights).length > 0) {
            console.log('Using provided traits and weights');
            commit('SET_AVAILABLE_TRAITS', traits);

            // Set active traits based on weights
            const activeTraitIds = Object.keys(weights);
            commit('SET_ACTIVE_TRAITS', activeTraitIds);

            // Initialize trait rankings
            commit('INITIALIZE_TRAIT_RANKINGS', weights);

            commit('SET_INITIALIZED', true);
            return;
        }

        // Otherwise fetch from API
        console.log('No props provided, fetching from API');
        return dispatch('fetchTraitsAndWeights');
    },

    /**
     * Toggle a trait selection
     */
    toggleTrait({ commit, state }, traitId) {
        traitId = String(traitId); // Ensure traitId is a string
        console.log(`[founderTraits] toggleTrait action called with: ${traitId}`, {
            traitIdType: typeof traitId,
            activeTraits: [...state.activeTraits],
            isCurrentlySelected: state.activeTraits.includes(traitId)
        });

        if (state.activeTraits.includes(traitId)) {
            console.log(`[founderTraits] Trait ${traitId} is selected, removing it`);
            commit('REMOVE_ACTIVE_TRAIT', traitId);

            // Also remove the importance setting
            const newRankings = { ...state.traitRankings };
            delete newRankings[traitId];
            commit('INITIALIZE_TRAIT_RANKINGS', newRankings);
        } else {
            console.log(`[founderTraits] Trait ${traitId} is not selected, adding it`);
            commit('ADD_ACTIVE_TRAIT', traitId);

            // Set default importance
            commit('SET_TRAIT_IMPORTANCE', { traitId, level: '5' });
        }

        console.log('[founderTraits] After toggleTrait, active traits:', [...state.activeTraits]);
    },

    /**
     * Remove a trait from selection
     */
    removeTrait({ commit, state }, trait) {
        const traitId = String(trait.id); // Ensure traitId is a string

        commit('REMOVE_ACTIVE_TRAIT', traitId);

        // Remove trait importance
        const newRankings = { ...state.traitRankings };
        delete newRankings[traitId];
        commit('INITIALIZE_TRAIT_RANKINGS', newRankings);

        // Close accordion if it was open
        if (state.openTrait === traitId) {
            commit('SET_OPEN_TRAIT', null);
        }
    },

    /**
     * Alias for removeTrait for compatibility with old code
     */
    removeSelectedTag({ dispatch }, traitId) {
        dispatch('removeTrait', { id: traitId });
    },

    /**
     * Toggle the accordion for a trait
     */
    toggleAccordion({ commit, state }, trait) {
        const traitId = trait.id ? String(trait.id) : trait;
        commit('SET_OPEN_TRAIT', state.openTrait === traitId ? null : traitId);
    },

    /**
     * Set the importance level for a trait
     */
    setImportance({ commit }, { traitId, level }) {
        traitId = String(traitId); // Ensure traitId is a string
        commit('SET_TRAIT_IMPORTANCE', { traitId, level });
    },

    /**
     * Delete all trait rankings
     */
    async confirmDelete({ commit, dispatch }) {
        // Could add a confirmation prompt here
        commit('CLEAR_TRAIT_RANKINGS');

        // Make an API call to clear the weights on the server
        try {
            await axios.post(API_URL_ENDPOINT);

            // Dispatch a success toast notification using the correct toast API
            dispatch('toast/success', {
                title: 'All trait rankings have been deleted.'
            }, { root: true });

            return true;
        } catch (error) {
            console.error('Failed to delete weights:', error);

            // Dispatch an error toast notification using the correct toast API
            dispatch('toast/error', {
                title: 'Failed to delete trait rankings',
                description: 'Please try again.'
            }, { root: true });

            return false;
        }
    },

    /**
     * Save the ranking algorithm
     */
    async saveRankingAlgorithm({ state, dispatch }) {
        try {
            await axios.post(API_URL_ENDPOINT, {
                weights: state.traitRankings
            });

            // Dispatch a success toast notification using the correct toast API
            dispatch('toast/success', {
                title: 'Founder ranking algorithm saved successfully!'
            }, { root: true });

            return true;
        } catch (error) {
            console.error('Failed to save ranking algorithm:', error);

            // Dispatch an error toast notification using the correct toast API
            dispatch('toast/error', {
                title: 'Failed to save founder ranking algorithm',
                description: 'Please try again.'
            }, { root: true });

            return false;
        }
    }
};

export default {
    namespaced: true,
    state,
    mutations,
    getters,
    actions
};
